/**
 * Customer aggregate (domain layer).
 *
 * Mirrors the read shape returned by /api/v1/admin/customers/. The domain
 * layer never imports from infrastructure — repositories adapt the API
 * response into these types.
 */

export type CustomerStatus = 'active' | 'suspended' | 'pending' | 'closed';

export interface CustomerListRow {
  id: string;
  email: string;
  company_name: string;
  status: CustomerStatus;
  balance: string; // Decimal serialized — keep as string in domain
  plan_name: string;
  messages_sent_today: number;
  messages_sent_month: number;
  created_at: string; // ISO 8601
}

export interface CustomerDetail extends Omit<CustomerListRow, 'plan_name'> {
  user: {
    id: string;
    email: string;
    username: string;
    phone: string;
    is_active: boolean;
    date_joined: string;
  };
  plan: {
    id: string;
    name: string;
    plan_type: string;
    base_rate: string;
    monthly_fee: string;
  };
  contact_person: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  business_type: string;
  website: string;
  credit_limit: string;
  last_message_sent: string | null;
  webhook_url: string;
  sender_id: string;
  timezone: string;
  updated_at: string;
}

/** Predicates — pure functions, easy to unit-test without IO. */
export const canSuspend = (c: Pick<CustomerListRow, 'status'>): boolean =>
  c.status === 'active' || c.status === 'pending';

export const canActivate = (c: Pick<CustomerListRow, 'status'>): boolean =>
  c.status === 'suspended' || c.status === 'pending';

export const isMutable = (c: Pick<CustomerListRow, 'status'>): boolean =>
  c.status !== 'closed';
